import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { Share2, Download } from "lucide-react";
import { api } from "@/lib/api";
import { gruppoColor } from "@/lib/race";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const W = 1080;
const H = 1920;

function loadImg(src) {
  return new Promise((resolve) => {
    if (!src) return resolve(null);
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = src;
  });
}

function roundRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

async function drawCard(canvas, { teamData, user, branding }) {
  const ctx = canvas.getContext("2d");
  const primary = branding.primary_color || "#FF1E27";
  const secondary = branding.secondary_color || "#FFB703";
  const bg = branding.bg_color || "#121214";
  const card = branding.card_color || "#2A2A32";

  ctx.fillStyle = bg;
  ctx.fillRect(0, 0, W, H);

  ctx.fillStyle = primary;
  ctx.beginPath();
  ctx.moveTo(0, 0);
  ctx.lineTo(W, 0);
  ctx.lineTo(W, 60);
  ctx.lineTo(0, 240);
  ctx.closePath();
  ctx.fill();

  const s = 34;
  for (let x = 0; x < Math.ceil(W / s); x++) {
    for (let y = 0; y < 2; y++) {
      ctx.fillStyle = (x + y) % 2 ? "#0a0a0c" : "#e8e8ec";
      ctx.fillRect(x * s, 246 + y * s, s, s);
    }
  }

  const [appLogo, raceLogo, orgLogo] = await Promise.all([
    loadImg(branding.logo_url),
    loadImg(branding.race_logo_url),
    loadImg(branding.organizer_logo_url),
  ]);

  ctx.textBaseline = "alphabetic";
  if (appLogo) {
    ctx.drawImage(appLogo, 48, 40, 110, 110);
    ctx.fillStyle = "#FFFFFF";
    ctx.font = "900 64px Orbitron, sans-serif";
    ctx.fillText("FANTASYRACE", 180, 122);
  } else {
    ctx.fillStyle = "#FFFFFF";
    ctx.font = "900 68px Orbitron, sans-serif";
    ctx.fillText("FANTASYRACE", 48, 122);
  }

  let lx = W - 48;
  for (const img of [raceLogo, orgLogo]) {
    if (img) {
      const h = 96;
      const w = (img.width / img.height) * h;
      lx -= w;
      ctx.drawImage(img, lx, 52, w, h);
      lx -= 24;
    }
  }

  ctx.fillStyle = secondary;
  ctx.font = "700 40px Orbitron, sans-serif";
  ctx.fillText("69° SALITA DEI MONTI IBLEI", 48, 400);

  const team = teamData?.team || {};
  ctx.fillStyle = "#FFFFFF";
  ctx.font = "900 84px Orbitron, sans-serif";
  const teamName = (team.nome_squadra || "La mia scuderia").toUpperCase();
  ctx.fillText(teamName.length > 18 ? teamName.slice(0, 18) + "…" : teamName, 48, 540);
  ctx.fillStyle = "#9CA3AF";
  ctx.font = "500 38px Outfit, sans-serif";
  ctx.fillText(`di @${user?.username || "pilota"}`, 48, 600);

  const pilots = teamData?.pilots || [];
  const COL_X = [48, 564];
  const CW = 468;
  const ROW_H = 128;
  const ROW_GAP = 140;
  const Y0 = 660;
  const shown = pilots.slice(0, 14);
  const avatarImgs = await Promise.all(
    shown.map((p) => loadImg(p.foto_url || `/api/pilots/${p.id}/avatar.svg`))
  );
  shown.forEach((p, i) => {
    const x = COL_X[i % 2];
    const y = Y0 + Math.floor(i / 2) * ROW_GAP;
    const gc = gruppoColor(p.gruppo);
    ctx.fillStyle = card;
    roundRect(ctx, x, y, CW, ROW_H, 16);
    ctx.fill();
    ctx.fillStyle = gc;
    roundRect(ctx, x, y, 12, ROW_H, 6);
    ctx.fill();

    const av = avatarImgs[i];
    if (av) {
      ctx.save();
      roundRect(ctx, x + 16, y + 32, 64, 64, 12);
      ctx.clip();
      ctx.drawImage(av, x + 16, y + 32, 64, 64);
      ctx.restore();
    }

    ctx.fillStyle = gc;
    ctx.font = "900 40px Orbitron, sans-serif";
    ctx.fillText(String(p.numero ?? ""), x + 94, y + 90);

    ctx.fillStyle = "#FFFFFF";
    ctx.font = "700 28px Outfit, sans-serif";
    const nm = `${p.nome} ${p.cognome}`;
    ctx.fillText(nm.length > 13 ? nm.slice(0, 13) + "…" : nm, x + 146, y + 58);
    ctx.fillStyle = "#9CA3AF";
    ctx.font = "400 22px Outfit, sans-serif";
    const vt = `${p.vettura} • ${p.categoria || p.gruppo}`;
    ctx.fillText(vt.length > 18 ? vt.slice(0, 18) + "…" : vt, x + 146, y + 98);

    if (p.is_captain) {
      ctx.fillStyle = secondary;
      roundRect(ctx, x + CW - 142, y + 14, 128, 44, 22);
      ctx.fill();
      ctx.fillStyle = "#000000";
      ctx.font = "800 20px Orbitron, sans-serif";
      ctx.fillText("CAP 1.5X", x + CW - 126, y + 44);
    }
  });

  ctx.fillStyle = "#9CA3AF";
  ctx.font = "400 36px Outfit, sans-serif";
  ctx.fillText("Questa è la mia scuderia per la Monti Iblei!", 48, H - 140);
  ctx.fillStyle = secondary;
  ctx.font = "700 42px Orbitron, sans-serif";
  ctx.fillText(`Sfidami su ${window.location.host}`, 48, H - 76);

  const s2 = 34;
  for (let x = 0; x < Math.ceil(W / s2); x++) {
    ctx.fillStyle = x % 2 ? "#0a0a0c" : "#e8e8ec";
    ctx.fillRect(x * s2, H - s2, s2, s2);
  }
}

export default function ShareTeamCard({ teamData, user }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const canvasRef = useRef(null);
  const dataRef = useRef({});

  const { data: settings } = useQuery({
    queryKey: ["settings-public"],
    queryFn: async () => (await api.get("/settings/public")).data,
  });

  dataRef.current = { teamData, user, branding: settings?.branding || {} };

  // Disegna quando il canvas è realmente montato (callback ref) — la Dialog Radix
  // monta il portale in modo asincrono, quindi un useEffect su `open` arriverebbe
  // con canvasRef.current ancora null e salterebbe il rendering per sempre.
  const attachCanvas = (el) => {
    canvasRef.current = el;
    if (el) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => drawCard(el, dataRef.current));
      });
    }
  };

  useEffect(() => {
    if (open && canvasRef.current && dataRef.current.teamData?.pilots?.length) {
      drawCard(canvasRef.current, dataRef.current);
    }
  }, [open, teamData, user, settings]);

  const getBlob = () =>
    new Promise((resolve) => canvasRef.current.toBlob(resolve, "image/png"));

  const share = async () => {
    setBusy(true);
    try {
      const blob = await getBlob();
      const file = new File([blob], "fantasyrace-scuderia.png", { type: "image/png" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({
          files: [file],
          title: "FantasyRace — La mia scuderia",
          text: "Questa è la mia scuderia per la 69° Salita dei Monti Iblei! Sfidami!",
        });
      } else {
        await download();
        toast.info("Condivisione diretta non disponibile: immagine scaricata, condividila nelle tue Storie!");
      }
      try {
        const { data } = await api.post("/profile/share-count");
        if (data?.bonus > 0) {
          toast.success("🎉 +5 Punti per la tua scuderia e trofeo 'Ora sei un dinamiko/a' sbloccato! I punti saranno visibili in classifica alla prossima pubblicazione dei punteggi.");
        }
      } catch {
        // bonus social non critico
      }
    } catch (e) {
      if (e?.name !== "AbortError") toast.error("Condivisione annullata");
    } finally {
      setBusy(false);
    }
  };

  const download = async () => {
    const blob = await getBlob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "fantasyrace-scuderia.png";
    a.click();
    URL.revokeObjectURL(url);
  };

  if (!teamData?.pilots?.length) return null;

  return (
    <>
      <button
        data-testid="share-team-button"
        onClick={() => setOpen(true)}
        className="flex items-center gap-2 rounded-lg bg-[#FF1E27] hover:bg-[#CC1118] transition-colors px-4 py-2.5 min-h-[44px] font-body-sport font-bold text-xs tracking-widest text-white uppercase"
      >
        <Share2 className="w-4 h-4" /> Condividi Formazione sui Social
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#1A1A1E] border-[#3F3F4C] max-w-sm" aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle className="font-racing text-white uppercase tracking-wide text-sm">
              Social Card — Stories 9:16
            </DialogTitle>
          </DialogHeader>
          <div className="rounded-xl overflow-hidden border border-[#3F3F4C]">
            <canvas ref={attachCanvas} width={W} height={H} className="w-full h-auto block" data-testid="share-canvas" />
          </div>
          <div className="flex gap-2 mt-3">
            <button
              data-testid="share-social-button"
              onClick={share}
              disabled={busy}
              className="flex-1 flex items-center justify-center gap-2 rounded-lg bg-[#FF1E27] hover:bg-[#CC1118] disabled:opacity-50 transition-colors py-3 min-h-[48px] font-body-sport font-bold text-xs tracking-widest text-white uppercase"
            >
              <Share2 className="w-4 h-4" /> Condividi
            </button>
            <button
              data-testid="share-download-button"
              onClick={download}
              className="flex-1 flex items-center justify-center gap-2 rounded-lg border border-[#FFB703] text-[#FFB703] hover:bg-[#FFB703]/10 transition-colors py-3 min-h-[48px] font-body-sport font-bold text-xs tracking-widest uppercase"
            >
              <Download className="w-4 h-4" /> Scarica PNG
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
